import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Button,
    Flex,
    HTMLDialog,
    Icon,
    PriorityIcon
} from '@priority-software/priority-style-react';
import {DialogToolbarContent} from '../../../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {BPMStatusesList} from './StatusesList';
import {texts} from '../../texts';
import styles from './styles.module.scss';
import {isRTL} from '../../../../../../Localization';
import {AutomationChoose} from '../../../../reducer/model';

const statusesDialogID = 'statusesDialogID';

type Props = {
    onClose: () => void;
    onBack: () => void;
    options: Array<{label: string; value: string}>;
    openSelectedStatus: (status: string) => void;
};

export const OldStatusesDialog = (props: Props) => {
    const {onClose, options, onBack, openSelectedStatus} = props;
    const [selectedValue, setSelectedValue] = useState<string>('');

    const ref = useRef<string>('');
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const focusOption = useRef<AutomationChoose>(null);

    const onConfirmHandler = useCallback(() => {
        const value = selectedValue ? selectedValue : (focusOption.current?.value as string);
        openSelectedStatus(value);
        onClose();
    }, [onClose, selectedValue, openSelectedStatus]);

    const handleSelectedItem = useCallback((item: {label: string; value: string}) => {
        ref.current = item.value;
        setSelectedValue(item.value);
    }, []);

    const onItemFocus = useCallback((item: AutomationChoose) => {
        focusOption.current = item;
    }, []);

    useEffect(() => {
        if (confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }

        function handleEsc(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                onBack();
            }
        }

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onBack]);

    return (
        <HTMLDialog
            isOpen
            id={statusesDialogID}
            size="large"
            width={520}
            componentClasses={styles.dialog}
            onClose={onClose}
            escClosable={false}
            toolbarContent={
                <DialogToolbarContent
                    prefixIcon={
                        <Icon
                            icon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                            onClick={onBack}
                        />
                    }
                    helpText={texts.statuses_dialog_help}
                    mainTitle={texts.statuses_dialog_title}
                    tooltipPortalID={statusesDialogID}
                />
            }
            content={
                <BPMStatusesList
                    onSelect={handleSelectedItem}
                    data={options}
                    componentClasses={styles.statusesList}
                    selectedValue={selectedValue}
                    onFocusChange={onItemFocus}
                    placeholder={texts.search_list_placeholder}
                />
            }
            footer={
                <Flex justifyContent="flex-end" componentClasses={styles.footer}>
                    <Button size="medium" wideButton onClick={onBack} skin="secondary">
                        {texts.status_dialog_btn_cancel}
                    </Button>
                    <Button
                        size="medium"
                        wideButton
                        onClick={onConfirmHandler}
                        skin="primary"
                        inputRef={confirmButtonRef}
                    >
                        {texts.statuses_list_dialog_btn_confirm}
                    </Button>
                </Flex>
            }
        />
    );
};
